import { Autocomplete, Box, Popover, TextField, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { BehaviorConfigOption, useSelectedPartner } from 'src/graphql';
import { CustomViewFormConditionItem, UIType } from 'src/graphql/entities/custom-view';
import { isMulti } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { mapValuesToOptions } from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { formatConditionValue } from 'src/graphql/entities/custom-view/custom-view-value-format';
import { RulesDatePicker } from 'src/pages/Settings/rules/components/rules-date-picker';
import { QueryOptionsValue } from 'src/pages/Settings/rules/use-rules-engine';
import { FilterSection } from './styles';

interface ValueSelectorProps {
  condition: CustomViewFormConditionItem;
  onChange: (v: CustomViewFormConditionItem['value']) => void;
  anchorEl: HTMLElement | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  optionsSource?: QueryOptionsValue;
}

export const ValueSelector = ({
  condition,
  onChange,
  anchorEl,
  open,
  setOpen,
  optionsSource,
}: ValueSelectorProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const { selectedPartner } = useSelectedPartner();

  const { field, value: rawValue } = condition;

  /* options that are currently known (or fetched dynamically) */
  const optionsData = useMemo(
    () => (!field ? { options: [] as BehaviorConfigOption[] } : optionsSource),
    [field, optionsSource],
  );

  const selectorOptions = useMemo<BehaviorConfigOption[]>(() => {
    if (optionsData?.options?.length) {
      return optionsData.options;
    }
    return (field?.fieldOptions ?? []).map(({ id, label, value }) => ({ id, label, value }));
  }, [field, optionsData]);

  const valueForList = useMemo((): CustomViewFormConditionItem['value'] => {
    if (!Array.isArray(rawValue)) return rawValue;
    const allOptions = (optionsData?.historicalOptions ?? selectorOptions).map((option) => ({
      ...option,
      id: option.id ? String(option.id) : String(option.value),
    }));

    return mapValuesToOptions(rawValue, allOptions) as CustomViewFormConditionItem['value'];
  }, [rawValue, optionsData, selectorOptions]);

  /* for primitive + date */
  const flatValue =
    typeof valueForList === 'string'
      ? valueForList
      : ((valueForList as { value: string } | undefined)?.value ?? null);

  return (
    <>
      <FilterSection
        onClick={() => setOpen(true)}
        sx={{ maxWidth: '12rem', minWidth: 0, flexShrink: 1, overflow: 'hidden' }}
      >
        <Typography
          variant="body2"
          sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
        >
          {formatConditionValue(
            condition,
            optionsSource?.historicalOptions ?? selectorOptions,
          )}
        </Typography>
      </FilterSection>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          {/* LIST */}
          {field?.uiType === UIType.List && (
            <Autocomplete<BehaviorConfigOption, true>
              multiple
              limitTags={isMulti(condition.operator) ? 2 : undefined}
              options={selectorOptions}
              isOptionEqualToValue={(o, v) =>
                String(o.value).toLowerCase() === String(v.value).toLowerCase()
              }
              value={
                !valueForList || valueForList === ''
                  ? []
                  : (valueForList as BehaviorConfigOption[])
              }
              inputValue={inputValue}
              onInputChange={(_, v, reason) => {
                if ((!reason || reason === 'reset') && !v) return;
                setInputValue(v);
                optionsData?.onInputChange?.(v);
              }}
              onChange={(_, newVal) => {
                setInputValue('');
                onChange(newVal as unknown as CustomViewFormConditionItem['value']);
                if (!isMulti(condition.operator)) setOpen(false);
              }}
              renderInput={(params) => (
                <TextField {...params} label={field.label} size="small" autoFocus />
              )}
            />
          )}

          {/* DATE — RulesDatePicker supports rolling and relative date ranges (like "last 7 days"), bringing it in line with the mobile wizard and desktop query builder. Since it's a multi-step flow, don't close the popover on change like we did with the old DatePicker. */}
          {field?.uiType === UIType.Date && (
            <RulesDatePicker
              value={flatValue ?? ''}
              timezone={selectedPartner?.timeZone}
              onChange={({ isRollingDate, value }) =>
                onChange(isRollingDate ? (value as string) : (value as Dayjs).toISOString())
              }
            />
          )}

          {/* STRING / NUMBER */}
          {field?.uiType !== UIType.Date && field?.uiType !== UIType.List && (
            <TextField
              fullWidth
              label={field!.label}
              size="small"
              value={flatValue ?? ''}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => setOpen(false)}
              autoFocus
            />
          )}
        </Box>
      </Popover>
    </>
  );
};
